import React, { useState } from 'react';
import config from '../config';
import './TransitOSLogin.css';

/**
 * TransitOSLogin — multi-step login page styled after the TransitOS V4 design.
 *
 * Step 1 — Email: user enters their work email and clicks Continue.
 *           The email is stored and the user proceeds to sign in.
 *           The client list is NOT fetched here — doing so before
 *           authentication would expose client names to unauthenticated users.
 *
 * Step 2 — Sign in: the user is redirected to the tt-auth cookie
 *           login-redirect endpoint with their email pre-filled.
 *           After successful authentication the browser returns to /home.
 *
 * Post-auth client selection is handled by the authenticated app
 * (GeniesPage / HomePage) once the session cookie is present.
 */
export default function TransitOSLogin() {
  // ── state ────────────────────────────────────────────────────────────────
  const [step, setStep]   = useState(1); // 1 | 2
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // ── Step 1 handler — validate email and advance ──────────────────────────
  function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your work email address.');
      return;
    }
    setStep(2);
  }

  // ── Step 2 handler — redirect to tt-auth for password ───────────────────
  function handleProceedToLogin(e) {
    e.preventDefault();
    // Build returnUrl that lands the user at /home after successful login.
    // Client selection happens post-authentication on the /home page.
    const returnUrl = encodeURIComponent(`${window.location.origin}/home`);
    window.location.href = `${config.apiUrl}/ttauth/v1/cookie/login-redirect?returnUrl=${returnUrl}`;
  }

  // ── Back navigation ───────────────────────────────────────────────────────
  function goBack() {
    setError('');
    setStep(1);
  }

  // ── Shared pill strip (brand trust bar) ──────────────────────────────────
  const TrustPills = () => (
    <div className="tos-trust">
      <div className="tos-trust-label">Connected today</div>
      <div className="tos-trust-pills">
        <span className="tos-pill tos-pill-bc">Bytecurve</span>
        <span className="tos-pill tos-pill-vt">Vestige DMP</span>
        <span className="tos-pill tos-pill-fa">FASTER Web</span>
        <span className="tos-pill tos-pill-ts">TripShot</span>
      </div>
    </div>
  );

  // ── Step indicator ────────────────────────────────────────────────────────
  const StepIndicator = () => (
    <div className="tos-steps">
      {[
        { n: 1, label: 'Email' },
        { n: 2, label: 'Sign in' },
      ].map(({ n, label }) => (
        <React.Fragment key={n}>
          <div className={`tos-step ${step === n ? 'active' : step > n ? 'done' : ''}`}>
            <div className="tos-step-dot">
              {step > n ? (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : n}
            </div>
            <span className="tos-step-label">{label}</span>
          </div>
          {n < 2 && <div className={`tos-step-line ${step > n ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="tos-page">
      {/* ── Left hero ─────────────────────────────────────────────────── */}
      <div className="tos-hero">
        {/* Brand */}
        <div className="tos-brand">
          <img src="/TTLogo.png" alt="Transit Technologies" />
          <div className="tos-brand-name">
            Transit <span>Technologies</span>
          </div>
        </div>

        {/* Hero body */}
        <div className="tos-hero-body">
          <span className="tos-tag">
            <span className="tos-tag-dot" />
            TransitOS.ai · AI-native operations platform
          </span>
          <h1 className="tos-headline">
            America&rsquo;s <em>TransitOS</em>
          </h1>
          <p className="tos-sub">
            One operator view for every mile, every driver, every dollar.
          </p>
          <p className="tos-sub-2">
            Bytecurve, Vestige, FASTER, and TripShot, unified.
          </p>
          <p className="tos-microline">
            Built for America&rsquo;s campus and municipal transit operators.
          </p>
        </div>

        {/* Trust strip */}
        <TrustPills />
      </div>

      {/* ── Right form column ──────────────────────────────────────────── */}
      <div className="tos-form-col">
        <div className="tos-form-wrap">

          {/* Step indicator */}
          <StepIndicator />

          {/* ── STEP 1 — Email ───────────────────────────────────────── */}
          {step === 1 && (
            <form className="tos-form" onSubmit={handleEmailSubmit}>
              <h2>Sign in to TransitOS</h2>
              <p className="tos-form-sub">
                Enter your work email to get started.
              </p>

              <div className="tos-field">
                <label htmlFor="tos-email">Work Email</label>
                <input
                  id="tos-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@your-organization.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  required
                  autoFocus
                />
              </div>

              {error && <div className="tos-error">{error}</div>}

              <button
                type="submit"
                className="tos-primary"
                disabled={!email.trim()}
              >
                Continue →
              </button>
            </form>
          )}

          {/* ── STEP 2 — Confirm & proceed to password ─────────────────── */}
          {step === 2 && (
            <form className="tos-form" onSubmit={handleProceedToLogin}>
              <h2>Ready to sign in</h2>
              <p className="tos-form-sub">
                You&rsquo;ll be taken to our secure login page to enter your
                password.
              </p>

              {/* Summary card */}
              <div className="tos-summary">
                <div className="tos-summary-row">
                  <span className="tos-summary-label">Email</span>
                  <span className="tos-summary-value">{email}</span>
                </div>
              </div>

              {error && <div className="tos-error">{error}</div>}

              <button type="submit" className="tos-primary">
                Sign in with password →
              </button>

              <button type="button" className="tos-back" onClick={goBack}>
                ← Change email
              </button>
            </form>
          )}

          {/* Legal */}
          <div className="tos-legal">
            © 2026 Transit Technologies ·{' '}
            <a href="#privacy" onClick={e => e.preventDefault()}>Privacy</a> ·{' '}
            <a href="#terms"   onClick={e => e.preventDefault()}>Terms</a> ·{' '}
            <a href="#help"    onClick={e => e.preventDefault()}>Contact your administrator</a>
          </div>
        </div>
      </div>
    </div>
  );
}
